import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { payWithProvider } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { Loader, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, CreditCard, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { profile } = useAuth();
  const orders = loc.state?.orders;
  const [payingId, setPayingId] = useState(null);

  useEffect(() => { if (!orders) nav("/retailer/cart"); }, [orders, nav]);
  if (!orders) return <Loader />;

  async function pay(order) {
    setPayingId(order.order_id);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: order.order_id, origin_url: window.location.origin });
      await payWithProvider(data, api, {
        onSuccess: () => nav(`/retailer/payment/success?session_id=${data.session_id}`),
        onError: (m) => { toast.error(m); setPayingId(null); },
      });
    } catch (e) { toast.error(apiError(e)); setPayingId(null); }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h1 className="font-heading text-2xl font-semibold text-forest">Checkout</h1>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-sm font-medium text-forest"><MapPin className="h-4 w-4" /> Delivery address</div>
        <p className="mt-2 text-sm text-ink/70">
          {profile?.shop_name}<br />{profile?.address}, {profile?.city}, {profile?.state} - {profile?.pincode}
        </p>
      </div>

      {orders.map((o) => (
        <div key={o.order_id} data-testid={`checkout-order-${o.order_id}`} className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="num text-sm font-medium text-forest">{o.order_number}</span>
            <span className="overline text-olive">Awaiting payment</span>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm text-ink/70">Total payable</span>
            <Money value={o.total} className="text-xl font-semibold text-forest" />
          </div>
          <Button data-testid={`pay-btn-${o.order_id}`} className="mt-4 w-full bg-forest text-white hover:bg-forest/90"
            disabled={payingId} onClick={() => pay(o)}>
            <CreditCard className="mr-2 h-4 w-4" />
            {payingId === o.order_id ? "Redirecting to payment…" : `Pay ${money(o.total)}`}
          </Button>
        </div>
      ))}

      <p className="flex items-center justify-center gap-1.5 text-xs text-ink/50">
        <ShieldCheck className="h-3.5 w-3.5 text-olive" /> Secure payment via Stripe · UPI, Cards, Net Banking · Test card 4242 4242 4242 4242
      </p>
    </div>
  );
}
